import { z } from "zod";

import { documentParagraphSchema } from "../utils/redline/wire/document-paragraph";

export const paragraphReferenceSchema = documentParagraphSchema.pick({ paragraphBookmarkName: true, paragraphIndex: true });

export const renumberedParagraphSchema = z.object({
  paragraph: paragraphReferenceSchema,
  labelBefore: z.string().nullable(),
  labelAfter: z.string().nullable(),
});

export const documentChangeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("paragraph_changed"),
    before: documentParagraphSchema,
    after: documentParagraphSchema,
  }),
  z.object({
    kind: z.literal("paragraph_added"),
    after: documentParagraphSchema,
    followsParagraph: paragraphReferenceSchema.nullable(),
  }),
  z.object({
    kind: z.literal("paragraph_deleted"),
    before: documentParagraphSchema,
    followedParagraph: paragraphReferenceSchema.nullable(),
  }),
  z.object({
    kind: z.literal("paragraphs_renumbered"),
    paragraphs: z.array(renumberedParagraphSchema).min(1),
  }),
  z.object({
    kind: z.literal("edited_since_read"),
    edit: z.enum(["changed", "added", "deleted"]),
    origin: z.enum(["local", "remote"]),
    paragraphs: z.array(paragraphReferenceSchema),
    unidentifiedParagraphCount: z.number().int().nonnegative(),
  }),
]);

export const appliedCardNumberSchema = z.number().int().positive().nullable();

export const appliedCardParagraphSchema = z.object({
  before: documentParagraphSchema.nullable(),
  after: documentParagraphSchema.nullable(),
});

export const appliedCardCommentSchema = z.object({
  text: z.string(),
  quotedText: z.string().nullable(),
  paragraphBookmarkNames: z.array(z.string()),
  notWrittenReason: z.string().nullable(),
});

export const appliedCardPlaceNotAppliedSchema = z.object({
  paragraphBookmarkNames: z.array(z.string()),
  outcome: z.enum(["not_chosen", "not_applied"]),
});

export const appliedCardSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("edit"),
    cardNumber: appliedCardNumberSchema,
    paragraphBookmarkNames: z.array(z.string()),
    appliedInPart: z.boolean(),
    paragraphs: z.array(appliedCardParagraphSchema),
    tableChanges: z.array(z.string()),
    comments: z.array(appliedCardCommentSchema),
    placesNotApplied: z.array(appliedCardPlaceNotAppliedSchema),
  }),
  z.object({
    kind: z.literal("comment"),
    cardNumber: appliedCardNumberSchema,
    paragraphBookmarkNames: z.array(z.string()),
    text: z.string(),
    quotedText: z.string().nullable(),
    paragraphs: z.array(documentParagraphSchema),
  }),
  z.object({
    kind: z.literal("reply"),
    cardNumber: appliedCardNumberSchema,
    commentId: z.string().min(1),
    text: z.string(),
  }),
]);

export const documentChangesForAgentSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("changes"),
    changes: z.array(documentChangeSchema),
    appliedCards: z.array(appliedCardSchema),
  }).refine((changesMessage) => changesMessage.changes.length > 0 || changesMessage.appliedCards.length > 0, {
    message: "a changes message carries at least one change or applied card",
  }),
  z.object({
    kind: z.literal("too_many_changes"),
    changedParagraphCount: z.number().int().positive(),
    appliedCards: z.array(appliedCardSchema),
  }),
  z.object({
    kind: z.literal("paragraph_ids_changed"),
    document: z.array(documentParagraphSchema).min(1).nullable(),
    changes: z.array(documentChangeSchema),
    unlistedChangedParagraphCount: z.number().int().nonnegative(),
    appliedCards: z.array(appliedCardSchema),
  }),
]);

export const DOCUMENT_CHANGES_RECEIVED_EVENT_TYPE = "document_changes_received";

export type ParagraphReference = z.infer<typeof paragraphReferenceSchema>;
export type RenumberedParagraph = z.infer<typeof renumberedParagraphSchema>;
export type DocumentChange = z.infer<typeof documentChangeSchema>;
export type AppliedCardParagraph = z.infer<typeof appliedCardParagraphSchema>;
export type AppliedCardComment = z.infer<typeof appliedCardCommentSchema>;
export type AppliedCardPlaceNotApplied = z.infer<typeof appliedCardPlaceNotAppliedSchema>;
export type AppliedCard = z.infer<typeof appliedCardSchema>;
export type DocumentChangesForAgent = z.infer<typeof documentChangesForAgentSchema>;
